import { ProductCalendar, ScheduleEntry } from '../../../clients/quipli-service/availability/availability';
import { DateTime, Interval } from 'luxon';
import { calcUnitRowHeight, ProductRowHeight, UnitRowHeight } from './inventory-gantt';
import classNames from 'classnames';
import { ReactNode, useEffect, useRef, useState } from 'react';
import { ProductTrackingType } from '../../../clients/quipli-service/product/product';
import { TimelineEntry, TimelineUnavailableEntry } from './timeline-entry';
import { Spinner } from '../../../components/spinner/spinner';
import { ScheduleTooltip } from '../schedule-tooltip/schedule-tooltip';
import { Category } from '../../../clients/quipli-service/category/category';
import { Location } from '../../../clients/quipli-service/location/location';

export const ProductTimeline = ({
  productCalendars,
  locations,
  categories,
  expandedCategories,
  isLoading,
  range,
  expandedProducts,
  productRowHeights,
  setProductRowHeights,
  registerScrollNode,
  serialNumberEnabled,
  isDashboard = true,
}: {
  productCalendars: ProductCalendar[];
  locations: Location[];
  categories: Category[] | undefined;
  expandedCategories: number[];
  isLoading: boolean;
  range: Interval;
  expandedProducts: number[];
  productRowHeights: ProductRowHeight[];
  setProductRowHeights: (productId: number, unitId: number, height: number) => void;
  registerScrollNode: (n: HTMLOListElement) => void;
  serialNumberEnabled: boolean;
  isDashboard?: boolean;
}) => {
  const containerRef = useRef<HTMLDivElement>(null);
  const scrollRef = useRef<HTMLOListElement>(null);

  useEffect(() => {
    if (scrollRef.current) {
      registerScrollNode(scrollRef.current);
    }
  }, [scrollRef]);

  function renderProductCalendars(productCalendar: ProductCalendar): ReactNode {
    return (
      <ProductTimelineContainer
        key={productCalendar.product.id}
        productCalendar={productCalendar}
        locations={locations}
        isLoading={isLoading}
        range={range}
        isExpanded={expandedProducts.indexOf(productCalendar.product.id) >= 0}
        rowHeights={productRowHeights.find((prh) => prh.product === productCalendar.product.id)?.unitHeights || []}
        setRowHeights={(unitId, height) => setProductRowHeights(productCalendar.product.id, unitId, height)}
        serialNumberEnabled={serialNumberEnabled}
      />
    );
  }

  useEffect(() => {
    if (range.contains(DateTime.now()) && containerRef.current) {
      const scrollFactor = (1.0 * DateTime.now().diff(range.start).milliseconds) / range.length('milliseconds');
      containerRef.current.scrollLeft = scrollFactor * containerRef.current.scrollWidth;
    }
  }, [range, containerRef.current?.scrollWidth]);

  return (
    <div ref={containerRef} className={classNames('product-timeline', isDashboard ? '' : 'max-h-[75vh]')}>
      <ol ref={scrollRef} className="product-timeline-list">
        <li className="product product-row header">
          <Scale range={range} width={containerRef.current?.getBoundingClientRect().width || 500} />
        </li>
        {isLoading && (
          <div className="loading-indicator">
            <div className="loading-overlay"></div>
            <div className="loading-spinner-container">
              <Spinner />
            </div>
          </div>
        )}
        {categories
          ? categories.map((c) => (
              <div className="category-row" key={c.id}>
                <div className="category-name" />
                {expandedCategories.indexOf(c.id) >= 0 &&
                  productCalendars
                    .filter((pc) => pc.product.categoryIds.indexOf(c.id) >= 0)
                    .map(renderProductCalendars)}
              </div>
            ))
          : productCalendars.map(renderProductCalendars)}
      </ol>
    </div>
  );
};

const ProductTimelineContainer = ({
  productCalendar,
  locations,
  isLoading,
  range,
  isExpanded,
  rowHeights,
  setRowHeights,
  serialNumberEnabled,
}: {
  productCalendar: ProductCalendar;
  locations: Location[];
  isLoading: boolean;
  range: Interval;
  isExpanded: boolean;
  rowHeights: UnitRowHeight[];
  setRowHeights: (unit: number, height: number) => void;
  serialNumberEnabled: boolean;
}) => {
  const unitTrackingField = serialNumberEnabled ? 'serialNumber' : 'unitNumber';
  return (
    <li className="product">
      <div className="product-summary-timeline product-row vertical-center">
        {!isLoading &&
          productCalendar.quantityAvailability.flatMap((qa) => {
            if (
              productCalendar.product.trackingType === ProductTrackingType.SerialUnits &&
              productCalendar.product.rentalStock === 0 &&
              qa.quantity === 0
            ) {
              return [];
            } else {
              const start = qa.dateTimeInterval.start || range.start;
              const end = qa.dateTimeInterval.end || range.end;
              const availableStock = qa.stock ?? productCalendar.product.rentalStock;
              const [message, tooltipMessage] =
                availableStock === 0
                  ? [
                      `${-1 * qa.quantity} order(s)`,
                      <div>
                        <span className="tooltip-label">Order(s)</span>: {-1 * qa.quantity}
                      </div>,
                    ]
                  : [
                      `${qa.quantity}/${availableStock} available`,
                      <>
                        <div
                          className={classNames(
                            {
                              'high-stock':
                                productCalendar.utilization &&
                                (100.0 * qa.quantity) / availableStock >
                                  productCalendar.utilization.utilizationThresholdHigh,
                            },
                            {
                              'low-stock':
                                productCalendar.utilization &&
                                (100.0 * qa.quantity) / availableStock <
                                  productCalendar.utilization.utilizationThresholdLow,
                            },
                          )}
                        >
                          {productCalendar.utilization && (
                            <span className="tooltip-label">
                              {(1.0 * qa.quantity) / availableStock <
                              productCalendar.utilization.utilizationThresholdLow / 100
                                ? ` Less than ${productCalendar.utilization.utilizationThresholdLow}% Available`
                                : ''}
                              {(1.0 * qa.quantity) / availableStock >
                              productCalendar.utilization.utilizationThresholdHigh / 100
                                ? ` More than ${productCalendar.utilization.utilizationThresholdHigh}% Available`
                                : ''}
                              {(1.0 * qa.quantity) / availableStock <
                                productCalendar.utilization.utilizationThresholdHigh / 100 &&
                              (1.0 * qa.quantity) / availableStock >
                                productCalendar.utilization.utilizationThresholdLow / 100
                                ? `Between ${productCalendar.utilization.utilizationThresholdLow}% and ${productCalendar.utilization.utilizationThresholdHigh}% Available`
                                : ''}
                            </span>
                          )}
                        </div>
                        <div>
                          <span className="tooltip-label">Available:</span>
                          {qa.quantity}/{availableStock}
                        </div>
                      </>,
                    ];
              const tooltip = (
                <div className="product-tooltip">
                  {tooltipMessage}
                  <div>
                    <span className="tooltip-label">Start:</span>{' '}
                    {start.diff(range.start).milliseconds > 0 ? start.setZone(range.start.zone).toFormat('D t') : '-'}
                  </div>
                  <div>
                    <span className="tooltip-label">End:</span>{' '}
                    {end.diff(range.end).milliseconds < 0 ? end.setZone(range.start.zone).toFormat('D t') : '-'}
                  </div>
                </div>
              );

              return [
                <TimelineEntry
                  key={start.toMillis()}
                  className={classNames({
                    'high-stock':
                      availableStock === 0 ||
                      (productCalendar.utilization &&
                        (1.0 * qa.quantity) / availableStock >=
                          productCalendar.utilization.utilizationThresholdHigh / 100),
                    'low-stock':
                      productCalendar.utilization &&
                      availableStock !== 0 &&
                      (1.0 * qa.quantity) / availableStock <= productCalendar.utilization.utilizationThresholdLow / 100,
                  })}
                  start={start.setZone(range.start.zone)}
                  end={end.setZone(range.start.zone)}
                  range={range}
                  title={message}
                  tooltip={tooltip}
                />,
              ];
            }
          })}
      </div>
      <ol className="unit-list">
        {isExpanded &&
          productCalendar.unitCalendar
            .sort((a, b) => {
              if (a.unit.locationId != b.unit.locationId) {
                return (locations.find((l) => l.id === a.unit.locationId)?.name || '').localeCompare(
                  locations.find((l) => l.id === b.unit.locationId)?.name || '',
                );
              } else {
                return a.unit[unitTrackingField].localeCompare(b.unit[unitTrackingField]);
              }
            })
            .map((us) => (
              <UnitTimelineRow
                isActive={us.unit.isActive}
                isOnOutage={us.unit.isOnOutage}
                key={us.unit.id}
                scheduleEntries={us.schedule}
                range={range}
                height={rowHeights.find((rh) => rh.unit === us.unit.id)?.height}
                setHeight={(h) => setRowHeights(us.unit.id, h)}
              />
            ))}
        {isExpanded && (
          <UnitTimelineRow
            scheduleEntries={productCalendar.unassignedOrders}
            range={range}
            height={rowHeights.find((rh) => rh.unit === -1)?.height}
            setHeight={(h) => setRowHeights(-1, h)}
          />
        )}
      </ol>
      {!isLoading && (productCalendar.product.disabledStartDate || productCalendar.product.disabledEndDate) && (
        <TimelineUnavailableEntry
          key={productCalendar.product.id}
          start={productCalendar.product.disabledStartDate}
          end={productCalendar.product.disabledEndDate}
          range={range}
        />
      )}
    </li>
  );
};

const Scale = ({ range, width }: { range: Interval; width: number }) => {
  let incrementFormat: string, increment: 'month' | 'day' | 'hour';

  if (range.length('days') <= 1) {
    increment = 'hour';
    incrementFormat = 't';
  } else if (range.length('months') <= 1) {
    increment = 'day';
    incrementFormat = 'ccc d';
  } else if (range.length('months') <= 3) {
    increment = 'day';
    incrementFormat = 'L/d';
  } else {
    increment = 'month';
    incrementFormat = 'LLL';
  }
  const scaleRanges = [Interval.fromDateTimes(range.start, range.start.endOf(increment))];
  let current = range.start.plus({ [increment]: 1 });
  while (current.diff(range.end.startOf(increment)).milliseconds < 0) {
    scaleRanges.push(Interval.fromDateTimes(current.startOf(increment), current.endOf(increment)));
    current = current.plus({ [increment]: 1 });
  }
  scaleRanges.push(Interval.fromDateTimes(range.end.startOf(increment), range.end));

  return (
    <div className="timeline-header">
      <div className="timeline-scale" style={{ minWidth: '100%' }}>
        {scaleRanges.map((r) => {
          const widthFactor = r.length('milliseconds') / range.length('milliseconds');
          return (
            <div
              key={r.start.toMillis()}
              style={{
                width:
                  100 * scaleRanges.length - 2 > width
                    ? `${100 * scaleRanges.length * widthFactor}px`
                    : `${width * widthFactor - 1}px`,
                overflow: 'hidden',
              }}
              className="vertical-center"
            >
              {r.start.toFormat(incrementFormat)}
            </div>
          );
        })}
      </div>
    </div>
  );
};

const UnitTimelineRow = ({
  isActive,
  isOnOutage,
  scheduleEntries,
  range,
  height,
  setHeight,
}: {
  isActive?: boolean;
  isOnOutage?: boolean;
  scheduleEntries: ScheduleEntry[];
  range: Interval;
  height?: number;
  setHeight: (h: number) => void;
}) => {
  function makeRows(input: ScheduleEntry[]): ScheduleEntry[][] {
    const sorted = [...input].sort((a, b) => a.from.diff(b.from).milliseconds);
    if (input.length === 0) {
      return [];
    }
    const row: ScheduleEntry[] = [sorted[0]];
    const next: ScheduleEntry[] = [];

    sorted.slice(1).forEach((s) => {
      const doesOverlap = row.find((o) =>
        Interval.fromDateTimes(s.from, s.to || range.end).overlaps(Interval.fromDateTimes(o.from, o.to || range.end)),
      );
      if (doesOverlap) {
        next.push(s);
      } else {
        row.push(s);
      }
    });

    if (next.length === 0) {
      return [row];
    } else {
      return [row, ...makeRows(next)];
    }
  }

  const [scheduleRows, setScheduleRows] = useState<ScheduleEntry[][]>([]);

  useEffect(() => {
    setScheduleRows(
      makeRows(
        scheduleEntries.filter((s) => {
          const scheduleInterval = Interval.fromDateTimes(s.from, s.to || range.end);
          return range.overlaps(scheduleInterval);
        }),
      ),
    );
  }, [scheduleEntries, range]);

  useEffect(() => {
    if (scheduleRows.length !== height) {
      setHeight(scheduleRows.length);
    }
  }, [scheduleRows, height]);

  return (
    <li
      className={classNames(
        { 'unit-disabled': (isActive !== undefined && !isActive) || isOnOutage },
        'unit-row',
        'unit-timeline',
      )}
      style={{ height: calcUnitRowHeight(height) }}
    >
      <div className="unit-timeline-content vertical-center">
        {scheduleRows.flatMap((row, i) => {
          return row.map((s) => {
            const tooltip = <ScheduleTooltip scheduleEntry={s} />;
            switch (s.type) {
              case 'NotInService':
                return (
                  <TimelineEntry
                    key={`NotInService:${s.to.toMillis()}`}
                    className="work-order-timeline"
                    row={i}
                    start={s.from}
                    end={s.to || range.end.plus(1)}
                    range={range}
                    title={`Not In Service`}
                    tooltip={tooltip}
                  />
                );
              case 'Order':
                return (
                  <TimelineEntry
                    key={`Order:${s.orderProductId}`}
                    row={i}
                    basisHeightRem={3}
                    start={s.from}
                    end={s.to || range.end.plus(1)}
                    bufferBefore={s.bufferBefore}
                    bufferAfter={s.bufferAfter}
                    range={range}
                    title={s.orderNumber}
                    tooltip={tooltip}
                  />
                );
              case 'Sold':
                return (
                  <TimelineEntry
                    key={`Sold:${s.from.toMillis()}`}
                    className="work-order-timeline"
                    row={i}
                    start={s.from}
                    end={s.to || range.end.plus(1)}
                    range={range}
                    title={`Sold`}
                    tooltip={tooltip}
                  />
                );
              case 'WorkOrder':
                return (
                  <TimelineEntry
                    key={`WorkOrder:${s.workOrderId}`}
                    className="work-order-timeline"
                    row={i}
                    start={s.from}
                    end={s.to || range.end.plus(1)}
                    range={range}
                    title={`WorkOrder`}
                    tooltip={tooltip}
                  />
                );
            }
          });
        })}
      </div>
    </li>
  );
};
